<script module lang="ts">
	export interface GlobalCardProps {
		image: { url: string; width: number; height: number };
		line1: string;
		line2?: string;
		line3?: string;
		href: string;
		squared?: boolean;
		dataCy: string;
		mobileVertical?: boolean;
		fullWidthTitles?: boolean;
		classes?: {
			title?: string;
			background?: string;
			image?: string;
		};
		class?: string;
		alt?: string;
		/** Load the image eagerly at high priority, for a card that is above the fold. */
		priority?: boolean;
		/** `sizes` for the image's srcset; defaults to the card's own layout. */
		sizes?: string;
		line3Addition?: import('svelte').Snippet;
		children?: import('svelte').Snippet;
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { CARD_IMAGE_SIZES } from '../../constants/card-image-sizes';
	import { getDownscaledImageSrcset } from '../../utils/getDownscaledImageSrcset/getDownscaledImageSrcset';
	import { Link } from '../Link';
	import { Txt } from '../Txt';
	import { WhiteBorderImageContainer } from '../WhiteBorderImageContainer';

	let {
		image,
		line1,
		line2 = undefined,
		line3 = undefined,
		href,
		squared = false,
		classes = {},
		mobileVertical = false,
		alt = undefined,
		priority = false,
		sizes = undefined,
		dataCy,
		line3Addition,
		children,
		...rest
	}: GlobalCardProps = $props();

	let dataCyPrefix = $derived(`${dataCy}-global-card`);
	// Below xs a horizontal card gives its image a third of the width.
	let imageSizes = $derived(
		sizes ??
			(mobileVertical
				? CARD_IMAGE_SIZES
				: '(min-width: 48rem) 50vw, (min-width: 30rem) 100vw, 33vw')
	);
	let imageLoadingProps = $derived(
		priority
			? { fetchpriority: 'high' as const, loading: 'eager' as const }
			: { loading: 'lazy' as const }
	);
</script>

<Link {href} class={rest.class} data-cy={dataCyPrefix}>
	<div
		class={classNames('relative flex h-full w-full transition-colors hover:bg-gray-50', {
			'flex-col': mobileVertical,
			'xs:flex-col': !mobileVertical
		})}
	>
		<div
			class={twMerge(
				classNames('relative bg-gray-200 md:h-[15rem]', {
					'pb-[33%]': squared,
					'h-[13.25rem] w-[100%]': mobileVertical,
					'xs:h-[13.25rem] xs:w-[100%] w-[33%]': !mobileVertical
				}),
				classes?.background
			)}
		>
			{#if squared}
				<img
					src={image.url}
					srcset={getDownscaledImageSrcset(image.url)}
					sizes={imageSizes}
					decoding="async"
					{...imageLoadingProps}
					data-cy={`${dataCyPrefix}-image`}
					alt={alt || line1}
					class={twMerge('absolute h-full w-full object-cover', classes?.image)}
				/>
			{:else}
				<WhiteBorderImageContainer
					{image}
					sizes={imageSizes}
					loading={priority ? 'eager' : 'lazy'}
					class={classNames('p-1 sm:p-2 md:h-[15rem]', {
						'h-[13.25rem]': mobileVertical,
						'xs:h-[13.25rem] h-[10rem]': !mobileVertical
					})}
					alt={alt || line1}
					dataCy={`${dataCyPrefix}-image`}
				/>
			{/if}
		</div>
		<div
			class={twMerge(
				classNames('flex w-8/12 flex-col items-start justify-center gap-1 p-4', {
					'w-full': mobileVertical,
					'xs:w-full': !mobileVertical
				}),
				classes?.title
			)}
		>
			{@render children?.()}
			<Txt variant="label2" dataCy={`${dataCyPrefix}-line-1`}>
				{line1}
			</Txt>
			{#if line2}
				<Txt variant="label3" class="text-gray-700" dataCy={`${dataCyPrefix}-line-2`}>
					{line2}
				</Txt>
			{/if}
			{#if line3}
				<Txt variant="label3" class="text-gray-700" dataCy={`${dataCyPrefix}-line-3`}>
					{line3}
					{@render line3Addition?.()}
				</Txt>
			{/if}
		</div>
	</div>
</Link>
